import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createScenarioInputSchema, type CreateScenarioInput, type Scenario } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { numberFieldOptions, serverMessage, submitErrorKey, textFieldOptions } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { HVAC_STATES, ROUTE_TYPES, SCENARIO_TRAFFIC_LEVELS, SEASONS, TEST_TYPES, TIME_BANDS } from './condition-display';

export interface ScenarioSheetProps {
  isOpen: boolean;
  onClose: () => void;
  protocolOptions: readonly SelectOption[];
  /** Absent for a create; present for an edit, which PUTs the full definition back. */
  scenario?: Scenario;
}

const DEFAULT_IDLE_DURATION_MIN = 30;

const toFormValues = (scenario: Scenario): CreateScenarioInput =>
  ({
    protocolId: scenario.protocolId,
    code: scenario.code,
    testType: scenario.testType,
    season: scenario.season,
    timeBand: scenario.timeBand,
    hvacState: scenario.hvacState,
    routeType: scenario.routeType ?? undefined,
    idleDurationMin: scenario.idleDurationMin ?? undefined,
    trafficLevel: scenario.trafficLevel ?? undefined,
    isCore: scenario.isCore,
  }) as CreateScenarioInput;

const EMPTY_VALUES = {
  protocolId: '',
  code: '',
  testType: 'DRIVE',
  season: 'SUMMER',
  timeBand: 'DAY',
  hvacState: 'AC_ON',
  isCore: false,
} as Partial<CreateScenarioInput>;

/**
 * Test type comes first because it decides the rest of the form: a DRIVE scenario asks for a route
 * type, an IDLE scenario for a duration. The contract's refinement enforces exactly that.
 */
export const ScenarioSheet = ({ isOpen, onClose, protocolOptions, scenario }: ScenarioSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = scenario !== undefined;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateScenarioInput>({
    resolver: zodResolver(createScenarioInputSchema),
    defaultValues: scenario ? toFormValues(scenario) : EMPTY_VALUES,
  });

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      reset(scenario ? toFormValues(scenario) : EMPTY_VALUES);
    }
  }, [isOpen, scenario, reset]);

  const testType = watch('testType');
  const isIdle = testType === 'IDLE';

  const createScenario = useMutation(orpc.scenarios.create.mutationOptions());
  const updateScenario = useMutation(orpc.scenarios.update.mutationOptions());

  const option = <TValue extends string>(values: readonly TValue[], prefix: string): SelectOption[] =>
    values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    // The API stores exactly one of the two dimension halves, so the unused half is dropped rather
    // than sent as a stale leftover from a test-type switch.
    const payload = {
      ...values,
      routeType: values.testType === 'DRIVE' ? values.routeType : undefined,
      idleDurationMin: values.testType === 'IDLE' ? values.idleDurationMin : undefined,
      // An idling car is not in traffic, which the contract rejects outright — so a traffic level left
      // behind by a test-type switch is dropped rather than sent.
      trafficLevel: values.testType === 'DRIVE' ? values.trafficLevel : undefined,
    };

    try {
      if (isEdit) {
        await updateScenario.mutateAsync({ ...payload, id: scenario.id });
      } else {
        await createScenario.mutateAsync(payload);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.scenarios.list.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
      ]);
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  });

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('conditions.scenarios.form.editTitle') : t('conditions.scenarios.form.createTitle')}
      description={isEdit ? t('conditions.scenarios.form.editSubtitle') : t('conditions.scenarios.form.createSubtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="scenario-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="scenario-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-4"
      >
        <FormField
          id="testType"
          label={t('conditions.scenarios.form.testType')}
          error={errors.testType?.message}
          hint={t('conditions.scenarios.form.testTypeHint')}
        >
          <Select
            id="testType"
            options={option(TEST_TYPES, 'enums.testType')}
            hasError={Boolean(errors.testType)}
            {...register('testType')}
          />
        </FormField>

        <FormField
          id="protocolId"
          label={t('conditions.scenarios.form.protocol')}
          error={errors.protocolId?.message}
        >
          <Select
            id="protocolId"
            options={protocolOptions}
            placeholder={t('conditions.scenarios.form.protocolPlaceholder')}
            hasError={Boolean(errors.protocolId)}
            {...register('protocolId')}
          />
        </FormField>

        <FormField
          id="scenario-code"
          label={t('conditions.scenarios.form.code')}
          error={errors.code?.message}
          hint={t('conditions.scenarios.form.codeHint')}
        >
          <Input
            id="scenario-code"
            placeholder={t('conditions.scenarios.form.codePlaceholder')}
            className="font-mono"
            hasError={Boolean(errors.code)}
            {...register('code')}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="season"
            label={t('conditions.scenarios.form.season')}
            error={errors.season?.message}
          >
            <Select
              id="season"
              options={option(SEASONS, 'enums.season')}
              hasError={Boolean(errors.season)}
              {...register('season')}
            />
          </FormField>

          <FormField
            id="timeBand"
            label={t('conditions.scenarios.form.timeBand')}
            error={errors.timeBand?.message}
          >
            <Select
              id="timeBand"
              options={option(TIME_BANDS, 'enums.timeBand')}
              hasError={Boolean(errors.timeBand)}
              {...register('timeBand')}
            />
          </FormField>
        </div>

        <FormField
          id="hvacState"
          label={t('conditions.scenarios.form.hvacState')}
          error={errors.hvacState?.message}
        >
          <Select
            id="hvacState"
            options={option(HVAC_STATES, 'enums.hvacState')}
            hasError={Boolean(errors.hvacState)}
            {...register('hvacState')}
          />
        </FormField>

        {isIdle ? (
          <FormField
            id="idleDurationMin"
            label={t('conditions.scenarios.form.idleDurationMin')}
            error={errors.idleDurationMin?.message}
          >
            <Input
              id="idleDurationMin"
              type="number"
              inputMode="numeric"
              placeholder={String(DEFAULT_IDLE_DURATION_MIN)}
              hasError={Boolean(errors.idleDurationMin)}
              {...register('idleDurationMin', numberFieldOptions)}
            />
          </FormField>
        ) : (
          <>
            <FormField
              id="routeType"
              label={t('conditions.scenarios.form.routeType')}
              error={errors.routeType?.message}
            >
              <Select
                id="routeType"
                options={option(ROUTE_TYPES, 'enums.routeType')}
                placeholder={t('conditions.scenarios.form.routeTypePlaceholder')}
                hasError={Boolean(errors.routeType)}
                {...register('routeType')}
              />
            </FormField>

            <FormField
              id="trafficLevel"
              label={t('conditions.scenarios.form.trafficLevel')}
              error={errors.trafficLevel?.message}
              hint={t('conditions.scenarios.form.trafficLevelHint')}
            >
              <Select
                id="trafficLevel"
                options={option(SCENARIO_TRAFFIC_LEVELS, 'enums.trafficLevel')}
                placeholder={t('conditions.scenarios.form.trafficLevelUnspecified')}
                hasError={Boolean(errors.trafficLevel)}
                // The blank option is a real choice — "traffic unspecified" — so it has to reach the API as
                // an absent field rather than as an empty string the enum would reject.
                {...register('trafficLevel', textFieldOptions)}
              />
            </FormField>
          </>
        )}

        <Checkbox
          id="isCore"
          label={t('conditions.scenarios.form.isCore')}
          {...register('isCore')}
        />

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
